// Small rules about an issue's workspace that more than one page and the conversation share.

import type {
  IssueBasis,
  IssueEntryKind,
  IssuePosition,
  IssueView,
  Message,
} from '../api/contract.js';

/** The words a position goes by on a page. */
export function positionWord(position: IssuePosition): string {
  switch (position) {
    case 'decided':
      return 'Decided';
    case 'tentative':
      return 'Tentative';
    case 'undecided':
      return 'Undecided';
  }
}

/** The small word a row carries for what it rests on; nothing for unknown. */
export function basisWord(basis: IssueBasis): string | null {
  switch (basis) {
    case 'statement':
      return 'Unconfirmed';
    case 'expectation':
      return 'Assumed';
    case 'unknown':
      return null;
  }
}

/** The key one entry goes by: its section and its id. */
export function entryKey(kind: IssueEntryKind, id: string): string {
  return `${kind}:${id}`;
}

/**
 * For every entry the reader can see, the card or divider its current version was written
 * under. A line on a card offers Undo only while its card is still this.
 */
export function currentTurns(view: IssueView | null): ReadonlyMap<string, string> {
  const turns = new Map<string, string>();
  if (view === null) {
    return turns;
  }
  if (view.intention !== null) {
    turns.set(entryKey('intention', view.id), view.intention.turnMessageId);
  }
  if (view.rationale !== null) {
    turns.set(entryKey('rationale', view.id), view.rationale.turnMessageId);
  }
  if (view.nextStep !== null) {
    turns.set(entryKey('next_step', view.id), view.nextStep.turnMessageId);
  }
  if (view.arrangements !== null) {
    turns.set(entryKey('arrangements', view.id), view.arrangements.turnMessageId);
  }
  for (const row of view.involvement) {
    turns.set(entryKey('involvement', row.id), row.turnMessageId);
  }
  for (const row of view.questions) {
    turns.set(entryKey('question', row.id), row.turnMessageId);
  }
  for (const row of view.checking) {
    turns.set(entryKey('checking', row.id), row.turnMessageId);
  }
  return turns;
}

/** The newest card or divider in a conversation: the turn whose changes are the latest. */
export function newestTurnId(messages: readonly Message[]): string | null {
  for (let at = messages.length - 1; at >= 0; at -= 1) {
    const message = messages[at];
    if (
      message !== undefined &&
      message.type === 'system' &&
      (message.metadata.systemKind === 'record_card' ||
        message.metadata.systemKind === 'record_edit')
    ) {
      return message.id;
    }
  }
  return null;
}

/**
 * "New" for the first version of an entry, "Corrected" for a later one that replaced a reading
 * the person said was wrong, "Updated" for any other later one, when it is the latest turn's.
 */
export function turnLabel(
  mark: {
    readonly seq: number;
    readonly turnMessageId: string;
    readonly correction: boolean;
  },
  newest: string | null,
): 'New' | 'Corrected' | 'Updated' | null {
  if (newest === null || mark.turnMessageId !== newest) {
    return null;
  }
  if (mark.seq === 1) {
    return 'New';
  }
  return mark.correction ? 'Corrected' : 'Updated';
}

/** What the issue's pages and conversation say when an issue is not available. */
export type IssueLoad =
  | { readonly status: 'loading'; readonly view: null }
  | { readonly status: 'missing'; readonly view: null }
  | { readonly status: 'faltered'; readonly view: IssueView | null }
  | { readonly status: 'ready'; readonly view: IssueView };
